import { useState, useEffect } from "react";
import { Menu, X, LogOut, Cpu, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const AOX_LEDGER_URL = "https://hoodies-marketplace.preview.emergentagent.com/#ledger";

const links = [
  { id: "welcome", label: "Welcome" },
  { id: "chat", label: "AI Mick" },
  { id: "explorer", label: "Explorer" },
  { id: "gallery", label: "Media Lab" },
  { id: "mining", label: "Mining" },
];

export function Navbar() {
  const { user, logout, setAuthOpen } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-aox-bg/90 backdrop-blur-xl border-aox-cyan/20"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3.5 flex items-center justify-between">
        <button
          onClick={() => go("welcome")}
          className="flex items-center gap-2.5 group"
          data-testid="nav-brand-logo"
        >
          <div className="w-9 h-9 grid place-items-center border border-aox-cyan/50 clip-chamfer bg-aox-cyan/10 group-hover:glow-cyan transition-all">
            <Cpu size={18} className="text-aox-cyan" />
          </div>
          <div className="text-left leading-none">
            <div className="font-display font-extrabold text-white text-lg tracking-tight">
              AI MICK
            </div>
            <div className="font-mono-aox text-[9px] uppercase tracking-[0.3em] text-aox-green">
              AOX Blockchain
            </div>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="px-4 py-2 text-sm font-medium text-aox-cyan/70 hover:text-aox-cyan hover:bg-aox-cyan/5 rounded-md transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={AOX_LEDGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 border border-aox-green/40 text-aox-green font-semibold text-sm px-4 py-2.5 clip-chamfer hover:bg-aox-green/10 hover:-translate-y-0.5 transition-all"
            data-testid="nav-aox-ledger-link"
          >
            AOX Ledger
            <ExternalLink size={14} />
          </a>
          {user ? (
            <>
              <div className="text-right leading-tight">
                <div className="text-sm font-semibold text-white">{user.name}</div>
                <div className="font-mono-aox text-xs text-aox-green">
                  {user.aox_balance.toFixed(2)} AOX
                </div>
              </div>
              <button
                onClick={logout}
                className="p-2 text-aox-cyan/60 hover:text-red-400 transition-colors"
                data-testid="nav-logout-btn"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setAuthOpen(true)}
              className="bg-aox-cyan text-aox-bg font-bold text-sm px-5 py-2.5 clip-chamfer hover:-translate-y-0.5 hover:glow-cyan transition-all"
              data-testid="nav-become-hoodie-btn"
            >
              Become a Hoodie
            </button>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-aox-cyan"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-aox-bg/95 backdrop-blur-xl border-t border-aox-cyan/20 px-4 py-4 space-y-1">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="block w-full text-left px-4 py-3 text-aox-cyan/80 hover:bg-aox-cyan/5 rounded-md"
              data-testid={`nav-mobile-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href={AOX_LEDGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-3 text-aox-green font-semibold"
            data-testid="nav-mobile-aox-ledger"
          >
            AOX Ledger <ExternalLink size={14} />
          </a>
          {user ? (
            <button
              onClick={logout}
              className="block w-full text-left px-4 py-3 text-red-400"
              data-testid="nav-mobile-logout"
            >
              Log out ({user.aox_balance.toFixed(2)} AOX)
            </button>
          ) : (
            <button
              onClick={() => {
                setAuthOpen(true);
                setOpen(false);
              }}
              className="w-full bg-aox-cyan text-aox-bg font-bold py-3 clip-chamfer mt-2"
              data-testid="nav-mobile-become-hoodie"
            >
              Become a Hoodie
            </button>
          )}
        </div>
      )}
    </header>
  );
}
